// Labeler Image Queue Module - Vuex store for image queue management
import { annotationService } from '@/services/annotation.service'

export const labelerImageQueue = {
  namespaced: true,

  state: {
    currentImage: null,
    nextImage: null,
    previousImages: [], // Array of { image, annotations, diagnoses, tags, comments }
    unlabeledCount: 0,
    projectId: null,
    isLoading: false,
    error: null,
    // Image Set state (for FilmStrip navigation)
    imageSet: [], // Array of images from image_set.otherImages
    currentImageSetIndex: 0,
    imageSetAnnotations: {}, // Map of imageId -> { annotations, diagnoses, tags, comments }
    modifiedImageIds: new Set() // Track which images user has actually modified this session
  },

  getters: {
    hasNextImage: (state) => state.unlabeledCount > 0,
    hasPreviousImage: (state) => state.previousImages.length > 0,
    currentImageId: (state) => state.currentImage?._id || null,
    hasImageSet: (state) => state.imageSet.length > 1,
    currentImageInSet: (state) => state.imageSet[state.currentImageSetIndex] || null,
    imageSetLength: (state) => state.imageSet.length
  },

  mutations: {
    SET_CURRENT_IMAGE (state, image) {
      state.currentImage = image
    },
    SET_NEXT_IMAGE (state, image) {
      state.nextImage = image
    },
    ADD_PREVIOUS_IMAGE (state, imageData) {
      // imageData should contain { image, annotations, diagnoses, tags, comments }
      state.previousImages.push(imageData)
    },
    SET_UNLABELED_COUNT (state, count) {
      state.unlabeledCount = count
    },
    SET_PROJECT_ID (state, projectId) {
      state.projectId = projectId
    },
    SET_LOADING (state, isLoading) {
      state.isLoading = isLoading
    },
    SET_ERROR (state, error) {
      state.error = error
    },
    CLEAR_QUEUE (state) {
      state.currentImage = null
      state.nextImage = null
      state.previousImages = []
      state.unlabeledCount = 0
      state.error = null
      state.imageSet = []
      state.currentImageSetIndex = 0
      state.imageSetAnnotations = {}
      state.modifiedImageIds = new Set()
    },
    SET_IMAGE_SET (state, images) {
      state.imageSet = images
    },
    SET_IMAGE_SET_INDEX (state, index) {
      state.currentImageSetIndex = index
    },
    SET_IMAGE_SET_ANNOTATIONS (state, { imageId, data, isUserModified = false }) {
      state.imageSetAnnotations = {
        ...state.imageSetAnnotations,
        [imageId]: data
      }
      // Track if this was a user modification (not just loading from DB)
      if (isUserModified) {
        state.modifiedImageIds.add(imageId)
      }
    },
    MARK_IMAGE_MODIFIED (state, imageId) {
      state.modifiedImageIds.add(imageId)
    },
    CLEAR_IMAGE_SET_ANNOTATIONS (state) {
      state.imageSetAnnotations = {}
      state.modifiedImageIds = new Set()
    }
  },

  actions: {
    async initialize ({ commit, dispatch }, projectId) {
      commit('SET_PROJECT_ID', projectId)
      commit('CLEAR_QUEUE')
      await dispatch('fetchImageCount')
    },

    async fetchNextImage ({ commit, state, dispatch }) {
      try {
        commit('SET_LOADING', true)

        const image = await annotationService.getNextImage(state.projectId)

        // Don't automatically add to previous - that's handled in handleSaveAndNext
        commit('SET_CURRENT_IMAGE', image)
        commit('SET_ERROR', null)

        // Build image set if image_set.otherImages exists
        console.log('Image loaded:', image._id)
        console.log('image.image_set:', image?.image_set)
        console.log('image.otherImages:', image?.otherImages)
        console.log('image.image_set.otherImages:', image?.image_set?.otherImages)

        // Check both locations: image.image_set.otherImages and image.otherImages
        const otherImages = image?.image_set?.otherImages || image?.otherImages || []

        if (otherImages && otherImages.length > 0) {
          const imageSet = [image, ...otherImages]
          console.log('Setting image set with', imageSet.length, 'images')
          commit('SET_IMAGE_SET', imageSet)
          commit('SET_IMAGE_SET_INDEX', 0)
          commit('CLEAR_IMAGE_SET_ANNOTATIONS')
        } else {
          console.log('No other images found, clearing image set')
          commit('SET_IMAGE_SET', [])
          commit('SET_IMAGE_SET_INDEX', 0)
          commit('CLEAR_IMAGE_SET_ANNOTATIONS')
        }



        return image
      } catch (error) {
        console.error('Failed to fetch next image:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch next image')
        return null
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchSpecificImage ({ commit, state, dispatch, rootState }, imageId) {
      try {
        commit('SET_LOADING', true)

        const image = await annotationService.getSpecificImage(imageId, state.projectId)

        commit('SET_CURRENT_IMAGE', image)
        commit('SET_ERROR', null)

        // Build image set
        const otherImages = image?.otherImages || []
        const imageSet = otherImages.length > 0 ? [image, ...otherImages] : []

        if (imageSet.length > 0) {
          console.log('Setting image set with', imageSet.length, 'images for specific image')
          commit('SET_IMAGE_SET', imageSet)
          commit('SET_IMAGE_SET_INDEX', 0)
        } else {
          commit('SET_IMAGE_SET', [])
          commit('SET_IMAGE_SET_INDEX', 0)
        }

        // Restore labels for all images in the set if they exist
        commit('CLEAR_IMAGE_SET_ANNOTATIONS')

        // Helper function to get color from project pathology list
        const getPathologyColor = (pathologyName) => {
          const pathologyOptions = rootState.labelerAnnotation?.pathologyOptions || []
          const pathologyConfig = pathologyOptions.find(
            p => p.value === pathologyName || p.label === pathologyName
          )
          return pathologyConfig?.color || '#ed1a4f'
        }

        // Load primary image labels into labelerAnnotation for DISPLAY only
        // DO NOT populate imageSetAnnotations with old DB labels - that's only for user's session work
        if (image.labels) {
          console.log('Loading labels for display on primary image:', image._id)
          const primaryImageInstanceId = image.dicom?.SOPInstanceUID

          // Filter pathologies to only show ones belonging to THIS image
          // Match by instanceId (SOPInstanceUID) if available
          const filteredPathologies = (image.labels.pathologies || []).filter(pathology => {
            // If pathology has instanceId, it must match current image's SOPInstanceUID
            if (pathology.instanceId) {
              return pathology.instanceId === primaryImageInstanceId
            }
            // If no instanceId, check z value - z:0 or no z means current/primary image
            return !pathology.z || pathology.z === 0
          })

          console.log('Filtered pathologies for primary image:', filteredPathologies.length, 'of', image.labels.pathologies?.length)

          const primaryAnnotations = filteredPathologies.map(pathology => ({
            id: crypto.randomUUID(),
            type: (pathology.freehandpoints && pathology.freehandpoints.length > 0) ? 'freehand' : 'box',
            coords: pathology.coords || {},
            freehandpoints: pathology.freehandpoints || [],
            pathology: pathology.name,
            color: pathology.color || getPathologyColor(pathology.name),
            planes: pathology.planes || {},
            z: pathology.z !== undefined && pathology.z !== null ? pathology.z : null,
            other: pathology.other || [],
            can3dSegment: pathology.can3dSegment || false,
            window: pathology.window || null,
            mapped_stats: pathology.mapped_stats || {},
            autoSegment: pathology.autoSegment || [],
            instanceId: pathology.instanceId || null,
            isFromDB: true // Mark as loaded from DB, not user-created
          }))

          // Only set in labelerAnnotation for display, NOT in imageSetAnnotations
          commit('labelerAnnotation/SET_ANNOTATIONS', primaryAnnotations, { root: true })
          commit('labelerAnnotation/SET_SELECTED_DIAGNOSES', image.labels.diagnoses || [], { root: true })
          commit('labelerAnnotation/SET_SELECTED_TAGS', image.labels.tags || [], { root: true })
          commit('labelerAnnotation/SET_COMMENTS', image.labels.comment || '', { root: true })
        }

        // Note: We intentionally do NOT load other images' labels into imageSetAnnotations
        // Old labels from DB are for display reference only, not for re-sending
        // imageSetAnnotations will only contain user's new work from this session

        return image
      } catch (error) {
        console.error('Failed to fetch specific image:', error, '- falling back to next image')
        commit('SET_ERROR', null) // Clear error before failover

        // Failover: fetch next unlabeled image instead
        console.log('Attempting to load next unlabeled image as fallback')
        return await dispatch('fetchNextImage')
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchImageCount ({ commit, state }) {
      try {
        const count = await annotationService.getImageCount(state.projectId)
        commit('SET_UNLABELED_COUNT', count)
        commit('SET_ERROR', null)
      } catch (error) {
        console.error('Failed to fetch image count:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch image count')
      }
    },

    goToPreviousImage ({ commit, state }) {
      if (state.previousImages.length === 0) return

      const previousImage = state.previousImages.pop()
      commit('SET_CURRENT_IMAGE', previousImage)
      commit('SET_UNLABELED_COUNT', state.unlabeledCount + 1)
    },

    setProjectId ({ commit }, projectId) {
      commit('SET_PROJECT_ID', projectId)
    },

    // Image Set navigation actions
    selectImageFromSet ({ commit, state }, index) {
      if (index >= 0 && index < state.imageSet.length) {
        const selectedImage = state.imageSet[index]
        commit('SET_CURRENT_IMAGE', selectedImage)
        commit('SET_IMAGE_SET_INDEX', index)
      }
    },

    saveCurrentImageSetAnnotations ({ commit, state, rootState }) {
      const currentImageId = state.currentImage?._id
      if (!currentImageId) return

      // Only save PATHOLOGIES per image - diagnoses/tags are study-level
      const annotationData = {
        annotations: [...rootState.labelerAnnotation.annotations]
      }

      commit('SET_IMAGE_SET_ANNOTATIONS', {
        imageId: currentImageId,
        data: annotationData,
        isUserModified: true // Mark as user modification, not DB load
      })
    },

    loadImageSetAnnotations ({ commit, state, rootState }, imageId) {
      const savedData = state.imageSetAnnotations[imageId]

      // DIAGNOSES/TAGS/COMMENTS are STUDY-LEVEL - don't change them when navigating
      // Only PATHOLOGIES are IMAGE-LEVEL - change those based on which image is viewed

      if (savedData) {
        // Load user's saved annotations for this image (from this session)
        // Only update pathologies, NOT diagnoses/tags (those are study-level)
        commit('labelerAnnotation/SET_ANNOTATIONS', savedData.annotations, { root: true })
      } else {
        // No user data for this image - check if the image has old labels from DB for display
        const image = state.imageSet.find(img => img._id === imageId)

        if (image?.labels?.pathologies?.length > 0) {
          // Load old labels for display, marked as isFromDB
          const pathologyOptions = rootState.labelerAnnotation?.pathologyOptions || []
          const getPathologyColor = (pathologyName) => {
            const pathologyConfig = pathologyOptions.find(
              p => p.value === pathologyName || p.label === pathologyName
            )
            return pathologyConfig?.color || '#ed1a4f'
          }

          const dbAnnotations = image.labels.pathologies.map(pathology => ({
            id: crypto.randomUUID(),
            type: (pathology.freehandpoints && pathology.freehandpoints.length > 0) ? 'freehand' : 'box',
            coords: pathology.coords || {},
            freehandpoints: pathology.freehandpoints || [],
            pathology: pathology.name,
            color: pathology.color || getPathologyColor(pathology.name),
            planes: pathology.planes || {},
            z: pathology.z !== undefined && pathology.z !== null ? pathology.z : null,
            other: pathology.other || [],
            can3dSegment: pathology.can3dSegment || false,
            window: pathology.window || null,
            mapped_stats: pathology.mapped_stats || {},
            autoSegment: pathology.autoSegment || [],
            instanceId: pathology.instanceId || null,
            isFromDB: true // Mark as loaded from DB, not user-created
          }))

          // Only update pathologies, NOT diagnoses/tags (those are study-level)
          commit('labelerAnnotation/SET_ANNOTATIONS', dbAnnotations, { root: true })
        } else {
          // No saved data and no DB labels, clear pathologies only
          // Use SET_ANNOTATIONS with empty array instead of CLEAR_ANNOTATIONS
          // because CLEAR_ANNOTATIONS also clears diagnoses/tags (which are study-level)
          commit('labelerAnnotation/SET_ANNOTATIONS', [], { root: true })
        }
      }
    }
  }
}
